import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { NavItem, TabContent, TabPane } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import uuid from 'react-uuid';

import BackgroundBox from 'shared/components/BackgroundBox';
import MainSpinner from 'shared/components/MainSpinner';
import { NavLink } from 'shared/components/SharedStyles/SharedStyles';
import AlertModal from 'shared/components/AlertModal';
import { ALERT_TYPE_ERROR } from 'shared/utils';

import useApi from 'hooks/useApi';
import { closeAlert, setExecuted } from 'redux/actions/alertActions';
import { addBreadcrumb, removeBreadcrumb } from 'redux/actions/layoutActions';
import {
  goBackClientDocument,
  setClientDocument,
  setRefreshUpload,
} from 'redux/actions/documentActions';
import { selectCurrentClient } from 'redux/actions/clientActions';
import { goBackClientNotify } from 'redux/actions/notificationReviewActions';
import { getAttachs, goBackBasicData } from 'redux/actions/notificationActions';

import { colors } from 'shared/defaultTheme';
import ConfirmModal from 'shared/components/ConfirmModal';

import BasicDataForm from './components/BasicDataForm';
import CustomDataForm from './components/CustomDataForm';
import Notifications from './components/Notifications';
import Documentations from './components/Documentations';
import { ClientManagementWrapper, NavHome } from './styles';

export const ACTION_NEW = 'new';
export const ACTION_UPDATE = 'update';

const ClientManagement = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const theme = useSelector((state) => state.theme);
  const auth = useSelector((state) => state.auth);
  const { i18n, t } = useTranslation();
  const { openAlert, alertType, executeActionCloseAlert, alertMessage } = useSelector(
    (state) => state.alertState
  );
  const { isBackClientDocument } = useSelector((state) => state.document);
  const { newCommunication } = useSelector((state) => state.clientState);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const { breadcrumbs } = useSelector((state) => state.layout);
  const { isBackClientNotify } = useSelector((state) => state.notificationReview);
  const { isBackBasicData } = useSelector((state) => state.notification);
  const { customAreaApi } = useApi();
  const [activeTab, setActiveTab] = useState('1');
  const [currentTab, setCurrentTab] = useState('1');
  const [updating, setUpdating] = useState(false);
  const [currentClient, setCurrentClient] = useState();
  const [totalSizeUpload, setTotalSizeUpload] = useState(0);
  const [newBasicData, setNewBasicData] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [showWarningCustomData, setShowWarningCustomData] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [hasChangeCustomData, setHasChangeCustomData] = useState(false);
  const [isSaveBasicData, setIsSaveBasicData] = useState(false);
  const [isSaveCustomData, setIsSaveCustomData] = useState(false);
  const [i18Tags, setI18Tags] = useState([]);
  const refFileManager = useRef(null);
  const [titles, setTitles] = useState([
    i18n.t('client_management_basic_data'),
    i18n.t('client_management_custom_data'),
    i18n.t('general_documentation'),
    i18n.t('notifications_communication_title'),
  ]);

  useEffect(() => {
    // This code reload page when back button is clicked,
    // and prevent errors when user clicks back button in the browser,
    // and lose references to syncfusion components
    window.addEventListener('popstate', (event) => {
      if (event.state) {
        window.location.reload();
      }
    });

    return () => {
      window.removeEventListener('popstate', () => {});
    };
  }, []);

  const [, getTagsData] = customAreaApi(
    {
      url: `${window.API_V1}/cus/tags/${auth.cusNavId}`,
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading }, getClient] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );
  const { mainColor } = theme;

  useEffect(() => {
    return () => {
      dispatch(getAttachs([]));
    };
  }, []);

  const onToggleWarning = () => {
    setShowWarning((currentShow) => !currentShow);
  };

  const onToggleWarningCustomData = () => {
    setShowWarningCustomData((currentShow) => !currentShow);
  };

  useEffect(() => {
    setTitles([
      i18n.t('client_management_basic_data'),
      i18n.t('client_management_custom_data'),
      i18n.t('general_documentation'),
      i18n.t('notifications_communication_title'),
    ]);
  }, [selectedDefaultLanguage]);

  useEffect(() => {
    dispatch(
      addBreadcrumb({
        label: 'clients_add_new_client',
        active: true,
        link: pathname,
        isModify: true,
      })
    );
    dispatch(
      addBreadcrumb({
        label: 'client_management_basic_data',
        active: true,
        link: null,
        isModify: false,
      })
    );
  }, []);

  const activeTabInfo = (numberTab) => {
    setActiveTab(numberTab);
    setCurrentTab(numberTab);
    if (numberTab === '1') {
      dispatch(
        addBreadcrumb({
          label: 'client_management_basic_data',
          active: true,
          link: null,
          isModify: false,
        })
      );
    } else if (numberTab === '2') {
      dispatch(
        addBreadcrumb({
          label: 'client_management_custom_data',
          active: true,
          link: null,
          isModify: false,
        })
      );
    } else if (numberTab === '3') {
      dispatch(
        addBreadcrumb({
          label: 'general_documentation',
          active: true,
          link: null,
          isModify: false,
        })
      );
    } else {
      dispatch(
        addBreadcrumb({
          label: 'notifications_communication_title',
          active: true,
          link: null,
          isModify: false,
        })
      );
    }
  };

  useEffect(() => {
    const { action } = params;
    const currentUpdating = action === ACTION_UPDATE;
    if (currentUpdating && currentClient) {
      if (breadcrumbs.length > 1) {
        for (let index = 0; index < breadcrumbs.length; index += 1) {
          dispatch(removeBreadcrumb());
          dispatch(removeBreadcrumb());
        }
        dispatch(
          addBreadcrumb({
            active: false,
            label: 'general_home',
            link: '/home',
            isModify: false,
          })
        );
        dispatch(
          addBreadcrumb({
            label: 'home_client_section_label',
            active: true,
            link: '/clients',
            isModify: false,
          })
        );
      }
      dispatch(
        addBreadcrumb({
          label: 'clients_edit_new_client',
          active: true,
          link: pathname,
          isModify: true,
        })
      );
      dispatch(
        addBreadcrumb({
          label: currentClient?.clientName,
          active: false,
          link: `/${currentClient?.clientName}`,
          isModify: true,
        })
      );
      activeTabInfo(activeTab);
    }
  }, [currentClient]);

  const getActiveTab = (numberTab) => {
    if (numberTab !== '1' || numberTab !== '2') {
      if (
        (newBasicData?.cif !== currentClient?.cif ||
          newBasicData?.stateClient !== currentClient?.stateClient ||
          newBasicData?.tagsClient?.toString() !== currentClient?.tagsClient?.toString() ||
          newBasicData?.clientI18 !== currentClient?.clientI18 ||
          newBasicData?.clientName !== currentClient?.clientName ||
          newBasicData?.contactPhone !== currentClient?.contactPhone ||
          newBasicData?.cp !== currentClient?.cp ||
          newBasicData?.direction !== currentClient?.direction ||
          newBasicData?.local !== currentClient?.local ||
          newBasicData?.logo !== currentClient?.logo ||
          newBasicData?.state !== currentClient?.state ||
          newBasicData?.typeOrganization !== currentClient?.typeOrganization) &&
        !showMessage
      ) {
        setShowWarning(true);
        setShowMessage(true);
        setCurrentTab(numberTab);
      } else if (hasChangeCustomData) {
        setShowWarningCustomData(true);
        setCurrentTab(numberTab);
        setHasChangeCustomData(false);
      } else {
        activeTabInfo(numberTab);
      }
    } else {
      activeTabInfo(numberTab);
    }
  };

  useEffect(() => {
    const getTagList = async () => {
      try {
        const {
          data: { data },
        } = await getTagsData();

        setI18Tags(data);
      } catch (error) {
        console.error('getTagList ---> error:', error);
      }
    };
    getTagList();
  }, []);

  useEffect(() => {
    const fetchClient = async () => {
      const { action, id } = params;
      const currentUpdating = action === ACTION_UPDATE;
      setUpdating(currentUpdating);
      if (currentUpdating || (executeActionCloseAlert && alertType !== ALERT_TYPE_ERROR)) {
        dispatch(setExecuted());
        if (id) {
          const {
            data: { data, errors },
          } = await getClient({ url: `${window.API_V1}/cli/clients/${auth.cusNavId}/${id}` });
          if (errors.length) {
            return;
          }
          setCurrentClient(data);
          dispatch(selectCurrentClient(data));
        }
      }
    };
    fetchClient();
  }, [executeActionCloseAlert]);

  useEffect(() => {
    if (newCommunication) {
      getActiveTab('4');
    }
  }, [newCommunication]);

  useEffect(() => {
    if (isBackClientNotify) {
      dispatch(goBackClientNotify(false));
      setActiveTab('4');
    }
    if (isBackClientDocument) {
      dispatch(goBackClientDocument(false));
      dispatch(setClientDocument(false));
      setActiveTab('3');
    }
    if (isBackBasicData) {
      dispatch(goBackBasicData(false));
      setActiveTab('1');
    }
  }, [isBackClientNotify, currentClient, isBackBasicData, isBackClientDocument]);

  const onCloseAlert = () => {
    if (document.getElementById('file_upload_dialog')) {
      refFileManager.current.refresh();
    }
    dispatch(setRefreshUpload(true));
    setTotalSizeUpload(0);
    dispatch(closeAlert());
  };

  const onCancelAction = () => {
    onToggleWarning();
    setIsSaveBasicData(false);
  };

  const onDiscardChange = () => {
    onToggleWarning();
    setIsSaveBasicData(false);
    setActiveTab(currentTab);
  };

  const onOkSaveBasicData = () => {
    onToggleWarning();
    setIsSaveBasicData(true);
    setActiveTab(currentTab);
  };

  const onOkSaveCustomData = () => {
    onToggleWarningCustomData();
    setIsSaveCustomData(true);
    setActiveTab(currentTab);
  };

  return (
    <ClientManagementWrapper>
      <BackgroundBox className="backgroundBox-wrapper">
        <NavHome tabs>
          {titles.map((title, index) => (
            <React.Fragment key={uuid()}>
              <NavItem>
                <NavLink
                  color={activeTab === `${index + 1}` ? mainColor : colors.transparent}
                  selected={activeTab === `${index + 1}`}
                  onClick={() => getActiveTab(`${index + 1}`, title)}
                  disabled={updating === false && index > 0}
                >
                  {title}
                </NavLink>
              </NavItem>
            </React.Fragment>
          ))}
        </NavHome>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            {activeTab === '1' &&
              !!i18Tags.length &&
              (params.action === ACTION_NEW || (params.action !== ACTION_NEW && currentClient)) && (
                <BasicDataForm
                  updating={updating}
                  currentClient={currentClient}
                  setNewBasicData={setNewBasicData}
                  setShowMessage={setShowMessage}
                  isSaveBasicData={isSaveBasicData}
                  setIsSaveBasicData={setIsSaveBasicData}
                  i18Tags={i18Tags}
                />
              )}
          </TabPane>
          <TabPane tabId="2">
            {activeTab === '2' && (
              <CustomDataForm
                clientId={currentClient?.clientId ?? ''}
                getActiveTab={getActiveTab}
                isSaveCustomData={isSaveCustomData}
                setHasChangeCustomData={setHasChangeCustomData}
              />
            )}
          </TabPane>
          <TabPane tabId="3">
            {activeTab === '3' && currentClient?.clientId && (
              <Documentations
                clientId={currentClient?.clientId ?? ''}
                refFileManager={refFileManager}
                totalSizeUpload={totalSizeUpload}
                setTotalSizeUpload={setTotalSizeUpload}
              />
            )}
          </TabPane>
          <TabPane tabId="4">
            {activeTab === '4' && currentClient?.clientId && (
              <Notifications clientId={currentClient?.clientId ?? ''} />
            )}
          </TabPane>
        </TabContent>
        <AlertModal
          type={alertType}
          isOpen={openAlert}
          toggle={onCloseAlert}
          message={alertMessage}
          okText={t('general_button_ok')}
          onOk={onCloseAlert}
        />
        <ConfirmModal
          isOpen={showWarning}
          toggle={onCancelAction}
          confirmMessage={t('client_not_forget_save_basic_data')}
          cancelText={t('general_discard')}
          okText={t('general_button_save')}
          onCancel={onDiscardChange}
          onOk={onOkSaveBasicData}
        />
        <ConfirmModal
          isOpen={showWarningCustomData}
          toggle={() => {
            onToggleWarningCustomData();
            setIsSaveCustomData(false);
          }}
          confirmMessage={t('client_not_forget_save_basic_data')}
          cancelText={t('general_discard')}
          okText={t('general_button_save')}
          onCancel={() => {
            onToggleWarningCustomData();
            setIsSaveCustomData(false);
            setActiveTab(currentTab);
          }}
          onOk={onOkSaveCustomData}
        />
        {loading && <MainSpinner />}
      </BackgroundBox>
    </ClientManagementWrapper>
  );
};

export default ClientManagement;
